import { FILE_INPUT_ACCEPT } from '../core/io/formats';

/**
 * Browser-side image plumbing: decode, pick, download, clipboard.
 *
 * Everything here is thin on purpose — the *decisions* (what to do with an
 * oversized image, which format to encode) live in `core/io` where they are
 * unit-testable without a browser.
 */

export type ImageSource = ImageBitmap | HTMLImageElement;

export interface DecodedImage {
  source: ImageSource;
  width: number;
  height: number;
  /** data: URL of the (possibly downscaled) bytes we keep in the document. */
  src: string;
  name: string;
  mime: string;
}

export function fileToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Decode image bytes. `createImageBitmap` is the fast path; SVG (and anything
 * the bitmap decoder refuses) falls back to an `<img>` element, which handles
 * SVG sizing correctly in every target browser.
 */
export async function decodeImage(
  blob: Blob,
): Promise<{ source: ImageSource; width: number; height: number }> {
  if (typeof createImageBitmap === 'function' && blob.type !== 'image/svg+xml') {
    try {
      const bitmap = await createImageBitmap(blob);
      return { source: bitmap, width: bitmap.width, height: bitmap.height };
    } catch {
      /* fall through to <img> */
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    const image = await loadImageUrl(url);
    return { source: image, width: image.naturalWidth, height: image.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function loadImageUrl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('This image could not be decoded.'));
    image.src = url;
  });
}

export async function readImageFile(file: File | Blob, name = 'image'): Promise<DecodedImage> {
  const { source, width, height } = await decodeImage(file);
  return {
    source,
    width,
    height,
    src: await fileToDataUrl(file),
    name,
    mime: file.type || 'image/png',
  };
}

/** Re-encode a decoded image at a new size — the downscale path for big files. */
export async function resampleToDataUrl(
  source: ImageSource,
  width: number,
  height: number,
  mime = 'image/png',
  quality = 0.92,
): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas is unavailable in this browser.');
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(source as CanvasImageSource, 0, 0, width, height);
  return canvas.toDataURL(mime, quality);
}

/** Replace a layer's data URL with a smaller one, preserving alpha for PNG. */
export async function downscaleDataUrl(
  src: string,
  width: number,
  height: number,
  mime: string,
): Promise<string> {
  const { source } = await decodeImage(await (await fetch(src)).blob());
  return resampleToDataUrl(source, width, height, mime);
}

/**
 * Native file picker. Resolves null when the user cancels — the `cancel` event
 * is what makes that distinguishable from a real selection.
 */
export function pickImageFile(accept = FILE_INPUT_ACCEPT): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.style.display = 'none';
    document.body.appendChild(input);

    const finish = (file: File | null) => {
      input.remove();
      resolve(file);
    };
    input.addEventListener('change', () => finish(input.files?.[0] ?? null));
    input.addEventListener('cancel', () => finish(null));
    input.click();
  });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Give the browser a moment to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function supportsClipboardImage(): boolean {
  return (
    typeof ClipboardItem !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    'clipboard' in navigator &&
    typeof navigator.clipboard.write === 'function'
  );
}

export async function copyImageToClipboard(blob: Blob): Promise<void> {
  if (!supportsClipboardImage()) {
    throw new Error('This browser cannot copy images to the clipboard.');
  }
  await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
}

/**
 * `.webpaint` files arrive with no useful MIME type, so recognise them by name.
 * Kept here rather than imported from `app/` so `io` never depends upward.
 */
function isProjectName(name?: string): boolean {
  return Boolean(name?.toLowerCase().endsWith('.webpaint'));
}

/** Extract image (or project) files from a drop or paste event, if any. */
export function imageFilesFrom(dataTransfer: DataTransfer | null): File[] {
  if (!dataTransfer) return [];
  const files: File[] = [];
  for (const item of Array.from(dataTransfer.items)) {
    if (
      item.kind === 'file' &&
      (item.type.startsWith('image/') || isProjectName(item.getAsFile()?.name))
    ) {
      const file = item.getAsFile();
      if (file) files.push(file);
    }
  }
  if (files.length === 0) {
    for (const file of Array.from(dataTransfer.files)) {
      if (file.type.startsWith('image/') || isProjectName(file.name)) files.push(file);
    }
  }
  return files;
}
